import React, { useState, useRef, useEffect } from 'react';
import StandardSpinner from '../../utils/Spinner';
import Footer from '../../components/HeaderFooter/Footer';
import { useTheme } from '../../Context/themeContext';

const SimpleWatchDemo = () => {
  const { theme } = useTheme();
  const [loading, setloading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [videoError, setVideoError] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(0);
  const videoRef = useRef(null);

  // Theme-aware classes
  const isDark = theme === 'dark';
  
  const bgPrimary = isDark 
    ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900'
    : 'bg-gradient-to-br from-gray-100 via-gray-50 to-blue-50';
  
  const bgCard = isDark ? 'bg-gray-800/50' : 'bg-white/80';
  const bgCardLight = isDark ? 'bg-gray-800/30' : 'bg-gray-100/70';
  const bgCardHover = isDark ? 'hover:bg-gray-700/30' : 'hover:bg-gray-200/50';
  const bgVideo = isDark ? 'bg-black' : 'bg-gray-900';
  const bgControls = isDark ? 'from-gray-900 via-gray-900/80 to-transparent' : 'from-gray-800 via-gray-800/80 to-transparent';
  const bgBadge = isDark ? 'bg-blue-600/90' : 'bg-blue-500/90';
  const bgButton = isDark ? 'bg-blue-600' : 'bg-blue-500';
  const bgButtonHover = isDark ? 'hover:bg-blue-700' : 'hover:bg-blue-600';
  const bgTabActive = isDark ? 'bg-blue-600' : 'bg-blue-500';
  const bgTabInactive = isDark ? 'bg-gray-800/50' : 'bg-gray-200/50';
  const bgTabHover = isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-300';
  const bgFallback = isDark ? 'from-gray-800 to-gray-900' : 'from-gray-200 to-gray-300';
  const bgInfoIcon = isDark ? 'bg-blue-600/20' : 'bg-blue-100';
  
  const borderColor = isDark ? 'border-blue-500/30' : 'border-blue-300/50';
  const borderLight = isDark ? 'border-blue-500/20' : 'border-blue-300/30';
  const borderInput = isDark ? 'border-blue-500/20' : 'border-gray-300';
  
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  //const textSecondary = isDark ? 'text-gray-300' : 'text-gray-700';
  //const textMuted = isDark ? 'text-gray-400' : 'text-gray-500';
  //const textBlue = isDark ? 'text-blue-400' : 'text-blue-600';
  //const textBlueLight = isDark ? 'text-blue-300' : 'text-blue-600';
  const textTabInactive = isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900';
  
  const shadowColor = isDark ? 'shadow-blue-600/30' : 'shadow-blue-500/20';
  const shadowLight = isDark ? 'shadow-blue-900/50' : 'shadow-blue-300/50';
  const shadowCard = isDark ? 'shadow-lg' : 'shadow-md';

  // Multiple reliable video sources from different providers
  const videoSources = [
    {
      name: "Product Demo",
      url: "https://storage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4",
      thumbnail: "https://peach.blender.org/wp-content/uploads/bbb-splash.png?x11217",
      duration: "10:34"
    },
    {
      name: "Feature Overview",
      url: "https://storage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4",
      thumbnail: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/images/ElephantsDream.jpg",
      duration: "15:20"
    },
    {
      name: "Getting Started",
      url: "https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4",
      thumbnail: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/images/ForBiggerBlazes.jpg",
      duration: "15:00"
    }
  ];

  useEffect(() => {
    setTimeout(() => {
      setloading(false);
    }, 800);
  }, []);

  useEffect(() => {
    setVideoError(false);
  }, [selectedVideo]);

  const handlePlayPause = () => {
    if (videoRef.current && !videoError) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(true);
            })
            .catch(error => {
              console.log("Playback prevented:", error);
              setIsPlaying(false);
            });
        }
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current && !isNaN(videoRef.current.duration)) {
      setCurrentTime(videoRef.current.currentTime);
      setVideoProgress((videoRef.current.currentTime / videoRef.current.duration) * 100);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current && !isNaN(videoRef.current.duration)) {
      setDuration(videoRef.current.duration);
      setVideoError(false);
    }
  };

  const handleError = (e) => {
    console.error("Video error:", e);
    setVideoError(true);
  };

  const handleSeek = (e) => {
    if (videoRef.current && !videoError) {
      const rect = e.target.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      const seekTime = pos * videoRef.current.duration;
      if (!isNaN(seekTime) && isFinite(seekTime)) {
        videoRef.current.currentTime = seekTime;
        setCurrentTime(seekTime);
        setVideoProgress(pos * 100);
      }
    }
  };

  const formatTime = (time) => {
    if (isNaN(time) || !isFinite(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const switchVideo = (index) => {
    setSelectedVideo(index);
    setIsPlaying(false);
    setVideoProgress(0);
    setCurrentTime(0);
    if (videoRef.current) {
      videoRef.current.load();
    }
  };

  if (loading) {
    return <StandardSpinner />;
  }

  return (
    <div className={`min-h-screen ${bgPrimary} py-24 px-4 transition-colors duration-300`}>
      <div className="max-w-6xl mx-auto">
        {/* Header Section */}
        <div className="mb-12 text-center">
          <h2 className={`text-4xl md:text-5xl font-bold ${textPrimary} mb-4`}>
            Watch <span className="text-blue-400">Interactive Demo</span>
          </h2>
          <p className={`text-xl ${isDark ? 'text-gray-300' : 'text-gray-700'} max-w-3xl mx-auto`}>
            Experience our platform in action with these comprehensive video tutorials
          </p>
          
          {/* Stats Badges */}
          <div className="flex flex-wrap justify-center gap-4 mt-6">
            <div className={`${isDark ? 'bg-blue-600/20' : 'bg-blue-100/80'} backdrop-blur-sm px-4 py-2 rounded-full border ${borderColor}`}>
              <span className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>Multiple demos</span>
            </div>
            <div className={`${isDark ? 'bg-blue-600/20' : 'bg-blue-100/80'} backdrop-blur-sm px-4 py-2 rounded-full border ${borderColor}`}>
              <span className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>Interactive tutorials</span>
            </div>
            <div className={`${isDark ? 'bg-blue-600/20' : 'bg-blue-100/80'} backdrop-blur-sm px-4 py-2 rounded-full border ${borderColor}`}>
              <span className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>Step-by-step guides</span>
            </div>
          </div>
        </div>

        {/* Video Source Selector */}
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {videoSources.map((source, index) => (
            <button
              key={index}
              onClick={() => switchVideo(index)}
              className={`px-4 py-2 rounded-lg transition-all ${
                selectedVideo === index
                  ? `${bgTabActive} text-white ${shadowCard} ${shadowColor}`
                  : `${bgTabInactive} ${textTabInactive} ${bgTabHover} border ${borderInput}`
              }`}
            >
              {source.name} ({source.duration})
            </button>
          ))}
        </div>

        {/* Main Video Section */}
        <div className="grid gap-8 lg:grid-cols-3">
          {/* Video Player - Takes 2/3 of the space on large screens */}
          <div className="lg:col-span-2">
            <div className="relative group">
              <div className={`aspect-video ${bgVideo} rounded-2xl overflow-hidden ${shadowLight} shadow-2xl border-2 ${borderColor}`}>
                {!videoError ? (
                  <>
                    {/* Actual Video Element */}
                    <video
                      ref={videoRef}
                      key={selectedVideo}
                      className="object-cover w-full h-full"
                      onTimeUpdate={handleTimeUpdate}
                      onLoadedMetadata={handleLoadedMetadata}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                      onEnded={() => setIsPlaying(false)}
                      onError={handleError}
                      preload="metadata"
                    >
                      <source src={videoSources[selectedVideo].url} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>

                    {/* Custom Video Controls Overlay */}
                    <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t ${bgControls} p-4 opacity-0 group-hover:opacity-100 transition-opacity`}>
                      {/* Progress Bar */}
                      <div 
                        className={`w-full h-1 ${isDark ? 'bg-gray-700' : 'bg-gray-400'} rounded-full mb-3 cursor-pointer relative`}
                        onClick={handleSeek}
                      >
                        <div 
                          className="relative h-full bg-blue-500 rounded-full"
                          style={{ width: `${videoProgress}%` }}
                        >
                          <div className="absolute right-0 w-3 h-3 transform translate-x-1/2 -translate-y-1/2 bg-blue-400 rounded-full shadow-lg top-1/2"></div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <button 
                            onClick={handlePlayPause}
                            className="text-white transition-colors hover:text-blue-400"
                          >
                            {isPlaying ? (
                              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M10 9v6h4V9h-4z"/>
                              </svg>
                            ) : (
                              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z"/>
                              </svg>
                            )}
                          </button>
                          
                          <span className="text-sm text-gray-300">
                            {formatTime(currentTime)} / {formatTime(duration)}
                          </span>
                        </div>

                        <div className="flex items-center space-x-3">
                          <button className="text-gray-400 transition-colors hover:text-white">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 001.414 1.414m2.828-9.9a9 9 0 012.728-2.728" />
                            </svg>
                          </button>
                          <button className="text-gray-400 transition-colors hover:text-white">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Big Play Button (when video is not playing) */}
                    {!isPlaying && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm">
                        <button 
                          onClick={handlePlayPause}
                          className="transition-transform transform hover:scale-110 focus:outline-none"
                        >
                          <div className="flex items-center justify-center w-20 h-20 bg-blue-600 rounded-full shadow-2xl shadow-blue-600/50 animate-pulse">
                            <svg className="w-10 h-10 ml-1 text-white" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </div>
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  /* Fallback UI when video fails to load */
                  <div className={`absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br ${bgFallback}`}>
                    <div className={`w-20 h-20 ${isDark ? 'bg-blue-600/20' : 'bg-blue-100'} rounded-full flex items-center justify-center mb-4`}>
                      <svg className={`w-10 h-10 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p className={`${textPrimary} font-semibold mb-2`}>Video temporarily unavailable</p>
                    <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm mb-4`}>Please try another demo video</p>
                    <button 
                      onClick={() => switchVideo((selectedVideo + 1) % videoSources.length)}
                      className={`${bgButton} ${bgButtonHover} text-white px-4 py-2 rounded-lg transition-colors`}
                    >
                      Try Another Demo
                    </button>
                  </div>
                )}

                {/* Video Tag */}
                <div className={`absolute top-4 left-4 ${bgBadge} backdrop-blur-sm px-3 py-1 rounded-full text-xs text-white font-semibold`}>
                  {videoSources[selectedVideo].name}
                </div>
              </div>
            </div>

            {/* Video Description */}
            <div className={`mt-4 ${bgCard} backdrop-blur-sm rounded-xl p-4 border ${borderColor} transition-colors duration-300`}>
              <div className="flex items-start space-x-3">
                <div className={`w-10 h-10 ${bgInfoIcon} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <svg className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className={`${textPrimary} font-semibold mb-1`}>About this demo</h3>
                  <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm`}>
                    This {videoSources[selectedVideo].name} walkthrough demonstrates key features including dashboard navigation, 
                    project management tools, and advanced customization options. Perfect for new users 
                    getting started with our platform.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Detailed Description */}
          <div className="space-y-6 lg:col-span-1">
            {/* Key Features Card */}
            <div className={`${bgCard} backdrop-blur-sm rounded-xl border ${borderColor} p-6 transition-colors duration-300`}>
              <h3 className={`text-xl font-semibold ${textPrimary} mb-4 flex items-center`}>
                <span className={`w-8 h-8 ${bgButton} rounded-lg flex items-center justify-center mr-3`}>
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                </span>
                What you'll learn
              </h3>
              
              <div className="space-y-4">
                {[
                  { title: 'Dashboard Overview', desc: 'Navigate through the main interface and understand key metrics' },
                  { title: 'Project Setup', desc: 'Create and configure your first project with custom settings' },
                  { title: 'Team Collaboration', desc: 'Invite team members and manage permissions effectively' },
                  { title: 'Advanced Features', desc: 'Explore automation tools and integration options' }
                ].map((feature, index) => (
                  <div key={index} className={`group cursor-pointer ${bgCardHover} p-3 rounded-lg transition-all`}>
                    <div className="flex items-start justify-between mb-1">
                      <h4 className={`${textPrimary} font-medium text-sm`}>{feature.title}</h4>
                    </div>
                    <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-xs`}>{feature.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits Card */}
            <div className={`${isDark ? 'bg-gradient-to-br from-blue-900/40 to-gray-800/40' : 'bg-gradient-to-br from-blue-100/60 to-gray-100/60'} rounded-xl border ${borderColor} p-6 transition-colors duration-300`}>
              <h3 className={`text-lg font-semibold ${textPrimary} mb-3`}>Why watch this demo?</h3>
              <ul className="space-y-3">
                {[
                  'See real-world usage scenarios',
                  'Learn time-saving shortcuts',
                  'Understand configuration options',
                  'Maximize platform potential',
                  'Get expert tips and tricks'
                ].map((benefit, index) => (
                  <li key={index} className={`flex items-center space-x-2 ${isDark ? 'text-gray-300' : 'text-gray-700'} text-sm`}>
                    <span className="text-blue-400">•</span>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Additional Info Section */}
        <div className="grid gap-6 mt-12 md:grid-cols-3">
          <div className={`${bgCardLight} backdrop-blur-sm rounded-xl p-6 border ${borderLight} transition-colors duration-300`}>
            <div className={`w-12 h-12 ${bgInfoIcon} rounded-lg flex items-center justify-center mb-4`}>
              <svg className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className={`${textPrimary} font-semibold mb-2`}>Self-Paced Learning</h3>
            <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Watch at your own speed, pause, and replay sections as needed</p>
          </div>

          <div className={`${bgCardLight} backdrop-blur-sm rounded-xl p-6 border ${borderLight} transition-colors duration-300`}>
            <div className={`w-12 h-12 ${bgInfoIcon} rounded-lg flex items-center justify-center mb-4`}>
              <svg className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3 className={`${textPrimary} font-semibold mb-2`}>Updated Regularly</h3>
            <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Demo content is updated with the latest features and improvements</p>
          </div>

          <div className={`${bgCardLight} backdrop-blur-sm rounded-xl p-6 border ${borderLight} transition-colors duration-300`}>
            <div className={`w-12 h-12 ${bgInfoIcon} rounded-lg flex items-center justify-center mb-4`}>
              <svg className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <h3 className={`${textPrimary} font-semibold mb-2`}>Interactive Elements</h3>
            <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Click through interactive segments to test features in real-time</p>
          </div>
        </div>
      </div>
      {/* CTA Section*/}
      <Footer />
    </div>
  );
};

export default SimpleWatchDemo;
